import { z } from "zod";

// Password validation schema with security requirements
export const passwordSchema = z
  .string()
  .min(8, "Password must be at least 8 characters")
  .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
  .regex(/[a-z]/, "Password must contain at least one lowercase letter")
  .regex(/[0-9]/, "Password must contain at least one number")
  .regex(/[^A-Za-z0-9]/, "Password must contain at least one special character");

// Auth validation schemas
export const signUpSchema = z.object({
  name: z.string().trim().min(2, "Name must be at least 2 characters").max(100),
  email: z.string().trim().email("Invalid email address").max(255),
  password: passwordSchema,
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"],
});

export const signInSchema = z.object({
  email: z.string().trim().email("Invalid email address"),
  password: z.string().min(1, "Password is required"),
});

// Client validation schema
export const clientSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "Name is required")
    .max(100, "Name must be less than 100 characters")
    .regex(/^[a-zA-Z0-9\s\-\.]+$/, "Name contains invalid characters"),
  contact: z
    .string()
    .trim()
    .min(1, "Phone number is required")
    .max(20, "Phone number must be less than 20 characters")
    .regex(/^\+?[0-9\s\-()]{7,20}$/, "Must be a valid phone number"),
  email: z
    .string()
    .trim()
    .email("Invalid email address")
    .max(255, "Email must be less than 255 characters")
    .optional()
    .or(z.literal("")),
  telegram_chat_id: z
    .string()
    .trim()
    .regex(/^(@[a-zA-Z0-9_]{5,32}|[0-9]{5,20})$/, "Must be a Telegram chat ID (@username or numeric ID)")
    .optional()
    .or(z.literal("")),
  channel_pref: z.enum(["email", "telegram"], {
    errorMap: () => ({ message: "Delivery method must be email or telegram" }),
  }),
});

// Material validation schema
export const materialSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "Material name is required")
    .max(200, "Material name must be less than 200 characters"),
  quantity: z
    .number()
    .positive("Quantity must be positive")
    .max(100000, "Quantity is too large")
    .finite("Quantity must be a valid number"),
  estimated_cost: z
    .number()
    .nonnegative("Cost cannot be negative")
    .max(1000000, "Cost is too large")
    .finite("Cost must be a valid number"),
});

// Invoice validation schema
export const invoiceSchema = z.object({
  materials: z
    .array(materialSchema)
    .min(1, "At least one material is required")
    .max(50, "Too many materials"),
  labor_cost: z
    .number()
    .nonnegative("Labor cost cannot be negative")
    .max(1000000, "Labor cost is too large")
    .finite("Labor cost must be a valid number"),
  total: z
    .number()
    .positive("Total must be positive")
    .max(2000000, "Total is too large")
    .finite("Total must be a valid number"),
});

// File upload validation
export const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
export const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

export const validateImageFile = (file: File): { valid: boolean; error?: string } => {
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    return {
      valid: false,
      error: "Invalid file type. Only JPEG, PNG, and WebP images are allowed.",
    };
  }

  if (file.size > MAX_FILE_SIZE) {
    return {
      valid: false,
      error: "File size exceeds 5MB limit. Please compress your image.",
    };
  }

  return { valid: true };
};

// Sanitize filename to prevent path traversal
export const sanitizeFilename = (filename: string): string => {
  return filename
    .replace(/[^a-zA-Z0-9\-_.]/g, "_") // Replace invalid chars
    .replace(/\.{2,}/g, "_") // Prevent directory traversal
    .substring(0, 100); // Limit length
};

// URL validation for signed URLs
export const validateUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" || parsed.protocol === "http:";
  } catch {
    return false;
  }
};
